import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  CalendarDays,
  ChevronsLeft,
  HeartHandshake,
  Home,
  LogOut,
  Palette,
  Search,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { DropdownMenu } from 'radix-ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IdleGuard } from '../../auth/IdleGuard';
import { useMe, useSession } from '../../auth/session';
import { cn } from '../../lib/cn';
import { ThemeSwitcher } from '../../theme/ThemeSwitcher';
import { Badge } from '../ui/Card';
import { LogoMark, Wordmark } from '../ui/LogoMark';
import { CommandPalette } from './CommandPalette';
import { LanguageToggle } from './LanguageToggle';
import { OrgSwitcher } from './OrgSwitcher';

// App frame (§17a "use of screen"): full-width, collapsible sidebar, sticky top bar, and page
// transitions. Everything else is content.

export interface NavItem {
  to: string;
  icon: ReactNode;
  labelKey: string;
  soon?: boolean;
}

export const NAV: NavItem[] = [
  { to: '/', icon: <Home size={18} />, labelKey: 'shell.nav.home' },
  { to: '/organization', icon: <Building2 size={18} />, labelKey: 'shell.nav.organization' },
  { to: '/schedule', icon: <CalendarDays size={18} />, labelKey: 'shell.nav.schedule', soon: true },
  { to: '/clients', icon: <HeartHandshake size={18} />, labelKey: 'shell.nav.clients', soon: true },
  { to: '/people', icon: <UserCog size={18} />, labelKey: 'shell.nav.people' },
  { to: '/caregivers', icon: <Users size={18} />, labelKey: 'shell.nav.caregivers', soon: true },
  { to: '/appearance', icon: <Palette size={18} />, labelKey: 'shell.nav.appearance' },
];

const COLLAPSE_KEY = 'clairo.sidebar.collapsed';

export function AppShell() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex min-h-dvh bg-bg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-on-primary"
      >
        {t('shell.skip')}
      </a>

      <motion.aside
        animate={{ width: collapsed ? 76 : 252 }}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        className="sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border bg-surface/60 backdrop-blur md:flex"
      >
        <div className="flex h-16 items-center gap-2.5 px-5">
          <LogoMark size={30} />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
              >
                <Wordmark />
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <nav aria-label={t('shell.nav.label')} className="flex flex-1 flex-col gap-1 px-3 pt-2">
          {NAV.map((item) => {
            const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
            const label = t(item.labelKey);
            const inner = (
              <>
                {active && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute inset-0 rounded-md bg-primary/10 ring-1 ring-primary/25"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                {active && (
                  <motion.span
                    layoutId="nav-bar"
                    className="absolute -left-3 top-2 bottom-2 w-1 rounded-r-full bg-accent"
                  />
                )}
                <span
                  className={cn(
                    'relative',
                    active ? 'text-primary' : 'text-muted group-hover:text-text',
                  )}
                >
                  {item.icon}
                </span>
                {!collapsed && (
                  <span
                    className={cn(
                      'relative flex flex-1 items-center justify-between gap-2 truncate',
                      active ? 'font-bold text-text' : 'text-muted group-hover:text-text',
                    )}
                  >
                    {label}
                    {item.soon && <Badge>{t('common.soonShort')}</Badge>}
                  </span>
                )}
              </>
            );
            const classes = cn(
              'group relative flex h-10 items-center gap-3 rounded-md px-3 text-base transition-colors',
              collapsed && 'justify-center px-0',
              item.soon && 'cursor-not-allowed opacity-70',
            );
            return item.soon ? (
              <span key={item.to} className={classes} title={label} aria-disabled="true">
                {inner}
              </span>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={classes}
                title={collapsed ? label : undefined}
                aria-current={active ? 'page' : undefined}
              >
                {inner}
              </Link>
            );
          })}
        </nav>

        <button
          onClick={() => setCollapsed((c) => !c)}
          className="m-3 flex h-9 items-center justify-center gap-2 rounded-md text-sm text-muted transition-colors hover:bg-subtle hover:text-text"
          aria-label={collapsed ? t('shell.expand') : t('shell.collapse')}
        >
          <motion.span animate={{ rotate: collapsed ? 180 : 0 }}>
            <ChevronsLeft size={18} />
          </motion.span>
          {!collapsed && t('shell.collapse')}
        </button>
      </motion.aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-bg/75 px-4 backdrop-blur-xl sm:px-6">
          <div className="md:hidden">
            <LogoMark size={28} />
          </div>
          <OrgSwitcher />
          <button
            onClick={() => setPaletteOpen(true)}
            className="ml-auto flex h-9 min-w-0 items-center gap-2 rounded-full border border-border bg-surface/70 px-3 text-sm text-muted transition-colors hover:text-text sm:w-72"
          >
            <Search size={15} />
            <span className="hidden truncate sm:inline">{t('shell.palette.open')}</span>
            <kbd className="ml-auto hidden rounded border border-border px-1.5 text-2xs font-semibold sm:inline">
              ⌘K
            </kbd>
          </button>
          <LanguageToggle id="shell-lang" />
          <ThemeSwitcher />
          <UserMenu />
        </header>

        <MfaBanner />

        <main id="main" className="flex-1 px-4 pb-28 pt-6 sm:px-8 sm:py-8 md:pb-8">
          {/* Each page fades in as it mounts. No exit-then-enter handshake (AnimatePresence
              mode="wait"): with the router's <Outlet/> it could leave the new page stuck at
              opacity 0 until something else re-rendered (e.g. moving the mouse). */}
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      <MobileNav pathname={pathname} />
      <IdleGuard />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

/** Bottom tab bar on phones, where the sidebar is hidden. */
function MobileNav({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('shell.nav.label')}
      className="glass fixed inset-x-3 bottom-3 z-40 flex justify-around rounded-xl px-2 py-1.5 elevated md:hidden"
      style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}
    >
      {NAV.filter((n) => !n.soon).map((item) => {
        const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            className="relative flex min-w-16 flex-col items-center gap-0.5 rounded-lg px-3 py-1.5 text-2xs font-semibold"
          >
            {active && (
              <motion.span
                layoutId="mobile-nav-active"
                className="absolute inset-0 rounded-lg bg-primary/12"
                transition={{ type: 'spring', stiffness: 460, damping: 34 }}
              />
            )}
            <span className={cn('relative', active ? 'text-primary' : 'text-muted')}>
              {item.icon}
            </span>
            <span className={cn('relative', active ? 'text-text' : 'text-muted')}>
              {t(item.labelKey)}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const initials = (me?.user.displayName ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <motion.button
          whileTap={{ scale: 0.94 }}
          aria-label={t('shell.user.menu')}
          className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-primary to-glow-2 text-xs font-bold text-on-primary ring-2 ring-surface"
        >
          {initials}
        </motion.button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={10}
          className="glass z-50 min-w-60 rounded-lg p-1.5 elevated"
        >
          <div className="px-3 py-2">
            <p className="text-base font-bold text-text">{me?.user.displayName}</p>
            <p className="truncate text-sm text-muted">{me?.user.email}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <MenuItem
            icon={<ShieldCheck size={16} />}
            onSelect={() => void navigate({ to: '/security/mfa' })}
          >
            {t('shell.user.security')}
          </MenuItem>
          <MenuItem
            icon={<LogOut size={16} />}
            onSelect={() => void signOut().then(() => navigate({ to: '/sign-in' }))}
          >
            {t('shell.user.signOut')}
          </MenuItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function MenuItem({
  icon,
  children,
  onSelect,
}: {
  icon: ReactNode;
  children: ReactNode;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-base text-text outline-none data-[highlighted]:bg-subtle"
    >
      <span className="text-muted">{icon}</span>
      {children}
    </DropdownMenu.Item>
  );
}

/** Grace-period nudge: MFA is required but not yet enforced. */
function MfaBanner() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const mfa = me?.mfa;
  if (!mfa?.required || mfa.enrolled || mfa.blocking || !mfa.enforceFrom) return null;
  const days = Math.max(
    1,
    Math.ceil((new Date(mfa.enforceFrom).getTime() - Date.now()) / 86_400_000),
  );
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      className="flex flex-wrap items-center gap-3 border-b border-warning/30 bg-warning/10 px-6 py-2.5 text-sm"
    >
      <ShieldCheck size={16} className="text-warning" />
      <span className="font-semibold text-text">{t('mfa.banner.title')}</span>
      <span className="text-muted">{t('mfa.banner.body', { count: days })}</span>
      <Link
        to="/security/mfa"
        className="ml-auto font-bold text-primary underline-offset-4 hover:underline"
      >
        {t('mfa.banner.action')}
      </Link>
    </motion.div>
  );
}
